import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useI18n } from '../../../lib/i18n'
import type { Page } from '../../PageManager/types'
import { dashboardSitePages, generateSlug, ensureUniqueSlug } from '../../PageManager/types'
import { publicSiteUrl, type BillingPlan } from '../../../lib/api'
import { SITE_PLAN_LIMITS, reachedLimit } from '../../../lib/planLimits'
import { usePaged, Pagination } from '../Pagination'
import { HeaderRefreshButton, ListToolbar, SelectCheckbox, StarButton, useListSelection } from '../ListSelection'
import { readStarredIds, setStarred as setStarredIds } from '../../../lib/dashboardStars'
import { PlanLimitModal } from '../PlanLimitModal'
import { useToast } from '../../Toast'
import { StarFilterButton } from '../StarFilterButton'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { PlanUsageSummary } from '../PlanUsageSummary'
import type { ProjectTheme } from '../../../lib/preview/types'
import { formatDashboardListDate, formatDashboardListDateTime } from '../../../lib/dashboardListDate'
import { IconAdjustmentsHorizontal, IconCalendarEvent, IconChartBar, IconCheck, IconChevronDown, IconCopy, IconDots, IconExternalLink, IconEye, IconFile, IconFileText, IconHome, IconLayoutGrid, IconLayout, IconLink, IconLoader2, IconPencil, IconPlus, IconSearch, IconSettings, IconStar, IconTrash, IconX } from '@tabler/icons-react'
import { matchesSearch } from '@webbuilder/shared-types'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { openOnRowClick } from '../rowOpen'

export function PagesSection({
  pages,
  billingPlan,
  publishedSubdomain,
  siteOrigin = null,
  savePages,
  onOpenEditor,
  onOpenPageSettings,
  onPreviewPage,
  onOpenPlans,
  onOpenStats,
  onReload,
  reloading,
  projectId,
}: {
  /** Para guardar los destacados: viven por proyecto. Ver [[dashboardStars]]. */
  projectId: string
  pages: Page[]
  billingPlan: BillingPlan
  publishedSubdomain?: string | null
  /**
   * El origen del sitio publicado, ya resuelto por quien sabe de dominios.
   *
   * Si el proyecto tiene dominio propio, ése es el sitio de verdad: abrir una
   * página llevaba al subdominio de pruebas, que es otra copia y con `noindex`.
   */
  siteOrigin?: string | null
  projectTheme?: ProjectTheme
  savePages: (pages: Page[]) => Promise<void>
  saveProjectTheme: (theme: ProjectTheme) => Promise<void>
  onOpenEditor: (pageId?: string, options?: { newPage?: boolean }) => void
  /** Page settings now live in the editor — "Ajustes" deep-links there. */
  onOpenPageSettings: (pageId: string) => void
  /** Global brand tokens live in the editor's Marca panel. */
  onOpenBrand?: () => void
  onPreviewPage: (pageId: string) => void
  // Kept optional for call-site compatibility; the inline settings view that
  // used them moved to the editor, so they're no longer read here.
  onDirtyChange?: (dirty: boolean) => void
  saveAttentionKey?: number
  token: string
  onOpenPlans: () => void
  /** Abre las estadísticas de una página. Su propia vista, con URL. */
  onOpenStats?: ((pageId: string) => void) | undefined
  /** Vuelve a pedir el proyecto, como el botón de las demás secciones. */
  onReload?: (() => void) | undefined
  reloading?: boolean | undefined
}) {
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('pages.searchPlaceholder')
  const [searchQuery, setSearchQuery] = useState('')
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [pageFilter, setPageFilter] = useState<'all' | 'home' | 'regular'>('all')
  /** «Sólo destacadas»: filtra sobre lo que hay, que es todo — la lista de
      páginas se pagina en memoria. Ver [[dashboardStars]]. */
  const [starredOnly, setStarredOnly] = useState(false)
  const [sortBy, setSortBy] = useState<'recent' | 'oldest' | 'name' | 'nameDesc' | 'createdRecent' | 'createdOldest'>('recent')
  const [filterMenuOpen, setFilterMenuOpen] = useState(false)
  const [listMenu, setListMenu] = useState<{ top: number; left: number; up: boolean; maxHeight: number }>({ top: 0, left: 0, up: false, maxHeight: 0 })
  const [renamePageId, setRenamePageId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const [creating, setCreating] = useState(false)
  const [limitModalOpen, setLimitModalOpen] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const filterMenuRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const renameInputRef = useRef<HTMLInputElement>(null)
  const { showToast } = useToast()

  /** Comprueba el cupo justo cuando una acción va a aumentar la lista. */
  function pageLimitReached(): boolean {
    const used = dashboardSitePages(pages).length
    if (!reachedLimit(used, SITE_PLAN_LIMITS[billingPlan].pages)) return false
    setLimitModalOpen(true)
    return true
  }

  useEffect(() => {
    if (!menuOpenId) return
    function onDown(e: PointerEvent) {
      const el = e.target as Element | null
      if (!el?.closest?.('.dash-project-row-menu-wrap') && !el?.closest?.('.dash-page-row-menu-portal')) setMenuOpenId(null)
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setMenuOpenId(null) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [menuOpenId])

  useEffect(() => {
    if (!filterMenuOpen) return
    function onDown(e: PointerEvent) {
      if (!filterMenuRef.current?.contains(e.target as Node)) setFilterMenuOpen(false)
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setFilterMenuOpen(false) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [filterMenuOpen])

  useEffect(() => {
    if (renamePageId) {
      renameInputRef.current?.focus()
      renameInputRef.current?.select()
    }
  }, [renamePageId])

  // ── Page mutations (mirror PageManagerModal, but persist FULL data via savePages) ──
  function duplicatePage(pageId: string) {
    const source = pages.find((p) => p.id === pageId)
    if (!source) return
    if (pageLimitReached()) return
    const name = `${source.name} ${t('common.copySuffix')}`
    const slug = ensureUniqueSlug(generateSlug(name), pages)
    const now = new Date().toISOString()
    void savePages([...pages, { ...source, id: crypto.randomUUID(), name, slug, isHome: false, isSlugCustom: false, createdAt: now, updatedAt: now }])
    showToast(t('workspace.pageDuplicated'))
  }

  function renamePage(pageId: string, name: string) {
    const trimmed = name.trim()
    const source = pages.find((p) => p.id === pageId)
    if (!source || !trimmed || trimmed === source.name) return
    void savePages(pages.map((p) => p.id === pageId ? { ...p, name: trimmed, updatedAt: new Date().toISOString() } : p))
    showToast(t('workspace.nameUpdated'))
  }

  function startListRename(page: Page) {
    setMenuOpenId(null)
    setRenamePageId(page.id)
    setRenameDraft(page.name)
  }

  function commitListRename(page: Page) {
    if (renamePageId !== page.id) return
    setRenamePageId(null)
    renamePage(page.id, renameDraft)
  }

  // Opens the styled confirm modal (see below); the actual removal runs on confirm.
  function deletePage(pageId: string) {
    const target = pages.find((p) => p.id === pageId)
    if (!target || target.isHome || pages.length <= 1) return
    setDeleteConfirmId(pageId)
  }

  function performDeletePage() {
    const id = deleteConfirmId
    setDeleteConfirmId(null)
    if (!id) return
    const target = pages.find((p) => p.id === id)
    if (!target || target.isHome || pages.length <= 1) return
    void savePages(pages.filter((p) => p.id !== id))
    showToast(t('workspace.pageDeleted'))
  }

  function setHomePage(pageId: string) {
    const target = pages.find((p) => p.id === pageId)
    if (!target || target.isHome) return
    void savePages(pages.map((p) => {
      if (p.id === pageId) return { ...p, isHome: true, slug: '/', isSlugCustom: false, redirect301: null }
      if (p.isHome) return { ...p, isHome: false, slug: generateSlug(p.name) || '/pagina', isSlugCustom: false }
      return p
    }))
  }


  function pageUrlFor(page: Page): string | null {
    const origin = siteOrigin ?? (publishedSubdomain ? publicSiteUrl(publishedSubdomain) : null)
    if (!origin) return null
    const path = page.isHome ? '' : `/${page.slug.replace(/^\/+/, '')}`
    return `${origin}${path}`
  }

  /* Los destacados viven en el navegador, por proyecto: ver [[dashboardStars]].
     Declarado ANTES de la lista filtrada, que es quien lo usa: al filtrar por
     destacadas se leía este estado desde una línea anterior a su propia
     declaración, y la pantalla se quedaba en blanco. No saltaba con el filtro
     apagado porque la condición cortaba antes de llegar a mirarlo. */
  const [starred, setStarred] = useState<Set<string>>(() => readStarredIds(projectId, 'pages'))
  const toggleStar = (id: string) => {
    setStarred(new Set(setStarredIds(projectId, 'pages', [id], !starred.has(id))))
  }

  const staticPages = dashboardSitePages(pages)
  const filteredPages = staticPages
    .filter((p) =>
      matchesSearch(p.name, searchQuery) ||
      matchesSearch(p.slug, searchQuery)
    )
    .filter((p) => pageFilter === 'all' || (pageFilter === 'home' ? p.isHome : !p.isHome))
    .filter((p) => !starredOnly || starred.has(p.id))
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name, 'es')
      if (sortBy === 'nameDesc') return b.name.localeCompare(a.name, 'es')
      if (sortBy === 'createdRecent' || sortBy === 'createdOldest') {
        const createdDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        return sortBy === 'createdOldest' ? createdDiff : -createdDiff
      }
      const diff = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
      return sortBy === 'oldest' ? diff : -diff
    })
  const pagedPages = usePaged(filteredPages, 10, `${searchQuery}|${pageFilter}|${sortBy}|${starredOnly}`)

  const clearFilters = () => {
    setSearchQuery('')
    setPageFilter('all')
    setStarredOnly(false)
    setSortBy('recent')
  }

  /* Con la lista vacía no sale la barra entera: ni buscador, ni filtros, ni
     el botón de crear. No hay nada que buscar ni que acotar, y crear ya lo
     ofrece el propio mensaje de empezar —que es lo único que tiene que
     haber en pantalla. Vuelve con la primera fila, o mientras quede algo
     escrito: si no, una búsqueda sin resultados se llevaría por delante la
     caja donde estaba escrita y no habría cómo deshacerla. */
  const showToolbar = staticPages.length > 0 || searchQuery.trim() !== ''
  const listSelection = useListSelection(pagedPages.pageItems.map((page) => page.id))

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)

  /**
   * Las seleccionadas que de verdad se pueden borrar.
   *
   * La de inicio nunca, y nunca la última que quede: son las mismas dos reglas
   * que ya aplica el borrado de una en una, y aquí hace falta contarlas antes
   * para no prometer en el botón un número que luego no se cumple.
   */
  function deletablePageIds(): string[] {
    const ids = listSelection.ids.filter((id) => !pages.find((page) => page.id === id)?.isHome)
    return ids.length >= pages.length ? ids.slice(0, Math.max(0, pages.length - 1)) : ids
  }

  function performDeleteSelected() {
    const ids = new Set(deletablePageIds())
    setBulkDeleteOpen(false)
    listSelection.clear()
    if (ids.size === 0) return
    void savePages(pages.filter((page) => !ids.has(page.id)))
    showToast(t(ids.size === 1 ? 'pages.deletedOne' : 'pages.deletedMany', { n: ids.size }))
  }
  /* La página nueva se crea en el editor, con su asistente: IA, templates de
     página y la vista previa en el tema del proyecto. El panel tenía uno propio
     que se había quedado atrás. El aviso de «abriendo» cubre la espera hasta
     que el editor carga. */
  function handleNewPage() {
    if (pageLimitReached() || creating) return
    setCreating(true)
    onOpenEditor(undefined, { newPage: true })
  }

  const setColumnSort = (column: 'name' | 'created' | 'updated') => {
    if (column === 'name') setSortBy((current) => current === 'name' ? 'nameDesc' : 'name')
    if (column === 'created') setSortBy((current) => current === 'createdRecent' ? 'createdOldest' : 'createdRecent')
    if (column === 'updated') setSortBy((current) => current === 'recent' ? 'oldest' : 'recent')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {creating ? (
        <div className="dash-create-overlay" role="status" aria-live="polite">
          <div className="dash-create-overlay-card">
            <IconLoader2 className="dash-spin" size={20} />
            <span>{t('pages.creating')}</span>
          </div>
        </div>
      ) : null}
      {limitModalOpen ? (
        <PlanLimitModal resource={t('cms.limit.pages')} currentPlan={billingPlan} onClose={() => setLimitModalOpen(false)} onOpenPlans={onOpenPlans} />
      ) : null}
      {bulkDeleteOpen ? (
        <div className="dash-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setBulkDeleteOpen(false) }}>
          <section className="dash-modal" role="dialog" aria-modal="true" aria-label={t('pages.bulkDelete.aria')}>
            <header className="dash-modal-header">
              <div className="dash-modal-icon" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}><IconTrash size={20} /></div>
              <h2>{deletablePageIds().length === 1
                ? t('pages.bulkDelete.titleOne')
                : t('pages.bulkDelete.titleMany', { count: deletablePageIds().length })}</h2>
              <p>
                {t('pages.bulkDelete.body')}
                {t('pages.undone')}
              </p>
            </header>
            <footer>
              <button className="dash-secondary-btn" type="button" onClick={() => setBulkDeleteOpen(false)}>{t('pages.cancel')}</button>
              <button className="dash-danger-btn" type="button" onClick={performDeleteSelected}>
                <IconTrash size={14} /> {t('pages.confirmDelete')}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
      {deleteConfirmId ? (
        <div className="dash-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setDeleteConfirmId(null) }}>
          <section className="dash-modal" role="dialog" aria-modal="true" aria-label={t('pages.delete.aria')}>
            <header className="dash-modal-header">
              <div className="dash-modal-icon" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}><IconTrash size={20} /></div>
              <h2>{t('pages.delete.title')}</h2>
              <p>
              {t('pages.delete.body', { name: '\u0000' })
                .split('\u0000')
                .flatMap((chunk, index) => (index === 0
                  ? [chunk]
                  : [<strong key="n">{pages.find((p) => p.id === deleteConfirmId)?.name || t('pages.delete.thisPage')}</strong>, chunk]))}
                {t('pages.undone')}
              </p>
            </header>
            <footer>
              <button className="dash-secondary-btn" type="button" onClick={() => setDeleteConfirmId(null)}>{t('pages.cancel')}</button>
              <button
                className="dash-danger-btn"
                type="button"
                onClick={performDeletePage}
              >
                <IconTrash size={14} /> {t('pages.confirmDelete')}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
      <section className="dash-projects-view dash-pages-view">
        <DashboardPageHeader
          title={t('pages.title')}
          subtitle={t('pages.subtitle')}
          /* Crear página no va aquí: está en la barra de búsqueda («New page»),
             y sin páginas, en el aviso vacío. Dos botones iguales confundían. */
          actionLayout="split"
          actions={(
            <>
              {onReload ? <HeaderRefreshButton refreshing={reloading ?? false} onRefresh={onReload} /> : null}
              <PlanUsageSummary items={[{ label: t('overview.card.pages'), used: staticPages.length, limit: SITE_PLAN_LIMITS[billingPlan].pages }]} plan={billingPlan} onOpenPlans={onOpenPlans} />
            </>
          )}
        />

        {showToolbar ? (
          <header className="dash-toolbar dash-layered-toolbar">
            <div className="dash-toolbar-search">
              <button
                type="button"
                className="dash-toolbar-search-icon"
                aria-label={t('pages.searchAria')}
                onClick={() => {
                  setFilterMenuOpen(false)
                  requestAnimationFrame(() => searchInputRef.current?.focus())
                }}
              >
                <IconSearch size={16} />
              </button>
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearchQuery('')
                    searchInputRef.current?.blur()
                  }
                }}
              />
              {searchQuery ? (
                <button type="button" className="dash-toolbar-search-clear" aria-label={t('pages.clearSearch')} onClick={() => { setSearchQuery(''); searchInputRef.current?.focus() }}>
                  <IconX size={13} />
                </button>
              ) : null}
            </div>

            <div className="dash-toolbar-controls">
              <StarFilterButton active={starredOnly} onToggle={() => setStarredOnly((only) => !only)} label={t('pages.starred')} />
              <div className="dash-filter-wrap" ref={filterMenuRef}>
                <button
                  className="dash-filter-trigger"
                  type="button"
                  aria-label={t('pages.filtersAria')}
                  data-tooltip={t('pages.filtersAria')}
                  aria-expanded={filterMenuOpen}
                  onClick={() => setFilterMenuOpen((open) => !open)}
                >
                  <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
                  {pageFilter !== 'all' || starredOnly ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
                </button>

                {filterMenuOpen ? (
                  <div className="dash-filter-popover" role="dialog" aria-label={t('pages.filtersDialogAria')}>
                    <div className="dash-filter-popover-head">
                      <strong>{t('pages.filterTitle')}</strong>
                      <button type="button" aria-label={t('pages.closeFilters')} onClick={() => setFilterMenuOpen(false)}><IconX size={15} /></button>
                    </div>

                    <div className="dash-filter-section">
                      <span className="dash-filter-section-title">{t('pages.filterType')}</span>
                      <div className="dash-filter-options">
                        {([
                          ['all', t('pages.type.all'), IconLayoutGrid],
                          ['home', t('pages.type.home'), IconHome],
                          ['regular', t('pages.internal'), IconFile],
                        ] as const).map(([value, label, Icon]) => (
                          <button key={value} className={pageFilter === value ? 'active' : ''} type="button" onClick={() => setPageFilter(value)}>
                            <Icon size={14} />
                            <span>{label}</span>
                            {pageFilter === value ? <IconCheck size={14} /> : null}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="dash-filter-section">
                      <span className="dash-filter-section-title">{t('pages.filterStarred')}</span>
                      <div className="dash-filter-options">
                        <button className={!starredOnly ? 'active' : ''} type="button" onClick={() => setStarredOnly(false)}><IconLayoutGrid size={14} /><span>{t('pages.type.all')}</span>{!starredOnly ? <IconCheck size={14} /> : null}</button>
                        <button className={starredOnly ? 'active' : ''} type="button" onClick={() => setStarredOnly(true)}><IconStar size={14} /><span>{t('pages.onlyStarred')}</span>{starredOnly ? <IconCheck size={14} /> : null}</button>
                      </div>
                    </div>

                    {pageFilter !== 'all' || starredOnly ? (
                      <button className="dash-filter-reset" type="button" onClick={() => { setPageFilter('all'); setStarredOnly(false) }}>
                        {t('pages.resetFilters')}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <button className="dash-primary-btn dash-toolbar-create" type="button" aria-label={t('pages.newAria')} onClick={handleNewPage} disabled={creating}>
                {creating ? <IconLoader2 className="dash-spin" size={15} /> : <IconPlus size={15} />}
                <span className="dash-btn-label dash-btn-label--desktop">{t('pages.new')}</span>
                <span className="dash-btn-label dash-btn-label--compact">{t('pages.newShort')}</span>
              </button>
            </div>
          </header>
        ) : null}

        {staticPages.length > 0 ? (
          <ListToolbar
            selection={listSelection}
            actions={[{ icon: <IconTrash size={14} />, label: t('common.delete'), danger: true, onClick: () => setBulkDeleteOpen(true) }]}
          />
        ) : null}

        {staticPages.length === 0 ? (
          <section className="dash-placeholder">
            <IconFileText size={34} aria-hidden="true" />
            <strong>{t('pages.emptyTitle')}</strong>
            <span>{t('pages.emptyBody')}</span>
            <button className="dash-primary-btn" type="button" onClick={handleNewPage}>
              <IconPlus size={15} /> {t('pages.createPage')}
            </button>
          </section>
        ) : filteredPages.length === 0 ? (
          <section className="dash-placeholder">
            <IconSearch size={34} aria-hidden="true" />
            <strong>{t('pages.noResultsTitle')}</strong>
            <span>{t('pages.noResultsBody')}</span>
            <button className="dash-secondary-btn" type="button" onClick={clearFilters}>{t('pages.clearFilters')}</button>
          </section>
        ) : (
          <>
          {/* Encima de la tabla: es donde se decide qué se va a mirar, y debajo
              obligaba a recorrer las diez filas para descubrir que había más. */}
          <Pagination page={pagedPages.page} totalPages={pagedPages.totalPages} total={pagedPages.total} start={pagedPages.start} end={pagedPages.end} onPage={pagedPages.setPage} unit={t('time.pages')} />
          <div className="dash-data-table dash-pages-data-table has-selection has-stars">
            <div className="dash-data-header" role="row">
              <span className="dash-data-select-cell">
                <SelectCheckbox
                  checked={listSelection.allVisible}
                  mixed={listSelection.someVisible}
                  label={t(listSelection.allVisible ? 'list.deselectAll' : 'list.selectAllOnPage')}
                  onChange={listSelection.toggleAll}
                />
              </span>
              <button className={sortBy === 'name' || sortBy === 'nameDesc' ? 'is-active' : ''} type="button" onClick={() => setColumnSort('name')}>
                {t('pages.col.name')} <IconChevronDown className={sortBy === 'name' ? 'is-asc' : ''} size={13} />
              </button>
              <button className={sortBy === 'createdRecent' || sortBy === 'createdOldest' ? 'is-active' : ''} type="button" onClick={() => setColumnSort('created')}>
                {t('pages.col.created')} <IconChevronDown className={sortBy === 'createdOldest' ? 'is-asc' : ''} size={13} />
              </button>
              <button className={sortBy === 'recent' || sortBy === 'oldest' ? 'is-active' : ''} type="button" onClick={() => setColumnSort('updated')}>
                {t('pages.col.updated')} <IconChevronDown className={sortBy === 'oldest' ? 'is-asc' : ''} size={13} />
              </button>
              <span className="dash-data-header-actions">{t('pages.col.actions')}</span>
            </div>
          <div className="dash-project-list">
            {pagedPages.pageItems.map((page) => {
              const pageUrl = pageUrlFor(page)
              const createdDate = formatDashboardListDate(page.createdAt)
              const updatedDate = formatDashboardListDateTime(page.updatedAt)
              return (
                <article className={`dash-project-row dash-data-row dash-loaded-content-in${listSelection.isSelected(page.id) ? ' is-selected' : ''}`} key={page.id} onClick={openOnRowClick(() => onOpenEditor(page.id))}>
                  <span className="dash-data-select-cell">
                    <SelectCheckbox
                      checked={listSelection.isSelected(page.id)}
                      label={t('list.selectX', { name: page.name || t('pages.thisPage') })}
                      onChange={() => listSelection.toggle(page.id)}
                    />
                    <StarButton
                      starred={starred.has(page.id)}
                      label={t('list.starX', { name: page.name || t('pages.thisPage') })}
                      onToggle={() => toggleStar(page.id)}
                    />
                  </span>
                  <div className="dash-project-row-main" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onOpenEditor(page.id) }}>
                    <span className={`dash-project-row-thumb dash-page-project-thumb${page.thumbnailUrl ? '' : ' dash-row-glass-fallback'}`}>
                      {page.thumbnailUrl ? <img src={page.thumbnailUrl} alt="" loading="lazy" /> : (
                        <span className={`dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon ${page.isHome ? 'is-home' : 'is-fog'}`} aria-hidden="true">
                          {page.isHome ? <IconHome size={13} /> : <IconFileText size={13} />}
                        </span>
                      )}
                    </span>
                    <span className="dash-project-row-copy">
                      <span className="dash-project-row-title">
                        {page.isHome ? (
                          <span className="bento-status-icon live" aria-label={t('pages.homeAria')}>
                            <IconHome size={12} />
                          </span>
                        ) : null}
                        {renamePageId === page.id ? (
                          <input
                            ref={renameInputRef}
                            className="dash-project-row-rename"
                            value={renameDraft}
                            aria-label={t('pages.nameAria')}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => commitListRename(page)}
                            onKeyDown={(e) => {
                              e.stopPropagation()
                              if (e.key === 'Enter') commitListRename(page)
                              if (e.key === 'Escape') setRenamePageId(null)
                            }}
                            autoFocus
                          />
                        ) : <strong>{page.name}</strong>}
                      </span>
                    </span>
                  </div>

                  <div className="dash-list-row-meta dash-page-row-meta">
                    <time className="dash-data-cell dash-list-date-cell dash-page-created-cell" data-label={t('pages.created')} dateTime={page.createdAt}><IconCalendarEvent className="dash-list-meta-icon" size={14} aria-hidden="true" />{createdDate}</time>
                    <time className="dash-data-cell dash-list-date-cell dash-page-updated-cell" data-label={t('pages.lastEdited')} dateTime={page.updatedAt}><IconPencil className="dash-list-meta-icon" size={14} aria-hidden="true" />{updatedDate}</time>
                  </div>

                  <div className="dash-project-row-actions">
                    <button className="dash-secondary-btn dash-project-row-action" type="button" onClick={() => onOpenEditor(page.id)}>
                      <IconLayout size={14} /> <span className="dash-project-row-action-label">{t('pages.open')}</span>
                    </button>
                    <div className={`dash-project-row-menu-wrap dash-page-options-wrap${menuOpenId === page.id ? ' is-open' : ''}${menuOpenId === page.id && listMenu.up ? ' menu-up' : ''}`}>
                      <button
                        className={`project-menu-btn dash-icon-btn dash-page-options-btn${menuOpenId === page.id ? ' active' : ''}`}
                        type="button"
                        aria-label={t('common.optionsOf', { name: page.name })}
                        aria-expanded={menuOpenId === page.id}
                        onClick={(e) => {
                          const willOpen = menuOpenId !== page.id
                          if (willOpen) {
                            const rect = e.currentTarget.getBoundingClientRect()
                            const menuWidth = 184
                            const menuHeight = 370
                            const edge = 12
                            const gap = 8
                            const spaceBelow = window.innerHeight - rect.bottom - edge
                            const spaceAbove = rect.top - edge
                            const up = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
                            const top = up
                              ? Math.max(edge, rect.top - menuHeight - gap)
                              : Math.min(window.innerHeight - menuHeight - edge, rect.bottom + gap)
                            const left = Math.max(edge, Math.min(window.innerWidth - menuWidth - edge, rect.right - menuWidth))
                            setListMenu({ top, left, up, maxHeight: Math.max(160, up ? spaceAbove - gap : spaceBelow - gap) })
                          }
                          setMenuOpenId((id) => id === page.id ? null : page.id)
                        }}
                      >
                        <IconDots size={16} />
                      </button>

                      {menuOpenId === page.id ? createPortal(
                          <div className={`project-menu dash-project-row-menu dash-page-row-menu-portal${listMenu.up ? ' is-up' : ''}`} style={{ top: listMenu.top, left: listMenu.left, maxHeight: listMenu.maxHeight, overflowY: 'auto' }}>
                            <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); onOpenEditor(page.id) }}><IconLayout size={14} /> {t('pages.menu.openEditor')}</button>
                            <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); onOpenPageSettings(page.id) }}><IconSettings size={14} /> {t('pages.menu.settings')}</button>
                            <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); onOpenStats?.(page.id) }}><IconChartBar size={14} /> {t('pages.menu.stats')}</button>
                            <div className="project-menu-rule" />
                            <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); onPreviewPage(page.id) }}><IconEye size={14} /> {t('pages.menu.preview')}</button>
                            {pageUrl ? <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); window.open(pageUrl, '_blank', 'noreferrer') }}><IconExternalLink size={14} /> {t('pages.menu.openPublished')}</button> : null}
                            {pageUrl ? <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); void navigator.clipboard?.writeText(pageUrl); showToast(t('common.linkCopied')) }}><IconLink size={14} /> {t('pages.menu.copyLink')}</button> : null}
                            <div className="project-menu-rule" />
                            <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); duplicatePage(page.id) }}><IconCopy size={14} /> {t('pages.menu.duplicate')}</button>
                            <button className="project-menu-item" type="button" onClick={() => startListRename(page)}><IconPencil size={14} /> {t('pages.menu.rename')}</button>
                            {!page.isHome ? <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); setHomePage(page.id) }}><IconHome size={14} /> {t('pages.menu.setHome')}</button> : null}
                            {!page.isHome ? <button className="project-menu-item danger" type="button" onClick={() => { setMenuOpenId(null); deletePage(page.id) }}><IconTrash size={14} /> {t('pages.menu.delete')}</button> : null}
                          </div>,
                          document.body,
                      ) : null}
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
          </div>
          {/* Y al pie: quien acaba de recorrer la página busca el siguiente
              paso donde terminó de leer. Con una sola, sólo la cuenta. */}
          <Pagination page={pagedPages.page} totalPages={pagedPages.totalPages} total={pagedPages.total} start={pagedPages.start} end={pagedPages.end} onPage={pagedPages.setPage} unit={t('time.pages')} alwaysShow />
          </>
        )}

      </section>
    </div>
  )
}
